/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { CircularProgress, Icon } from "./..";
import OptionWrapper from "../others/option-wrapper";
import PropTypes from 'prop-types';
import { forwardRef, memo, useImperativeHandle, useMemo, useRef } from 'react';
import { hexToRGBA } from "../../styles/color-helper";
import * as allColors from "../../styles/colors";
import { alphaPseudo, bgColor, border, borderColor, borderNone, borderRadius, boxBorder, cursorNotAllowed, cursorPointer, displayFlex, displayInlineFlex, flexRow, itemsCenter, justifyCenter, outlineNone, parseMinWidthHeight, parseWidthHeight, pd, pointerEventsNone, positionRelative, textColor, userSelectNone } from "../../styles/general";
import { getColor } from "../../styles/utils";
import { capitalize, classNames } from "../../utils";
import { getRippleColor } from '.';
import Ripple from "./ripple-effect";
const sizeMap = new Map([['tiny', {
  buttonSize: '24px',
  iconSize: '16px'
}], ['small', {
  buttonSize: '32px',
  iconSize: '20px'
}], ['medium', {
  buttonSize: '40px',
  iconSize: '24px'
}], ['large', {
  buttonSize: '48px',
  iconSize: '32px'
}], ['giant', {
  buttonSize: '56px',
  iconSize: '40px'
}], ['extraGiant', {
  buttonSize: '96px',
  iconSize: '56px'
}]]);
const iconColorMap = new Map([['Close', 'semantic.danger'], ['Cancel', 'semantic.danger'], ['Approval', 'semantic.success'], ['Delete', 'semantic.warning'], ['Trash', 'semantic.warning'], ['Edit', 'semantic.info'], ['EditV2', 'semantic.info'], ['View', 'semantic.info']]);
const alphaLoading = 0.2;
// customIconSize calculated based on design size
// Button size <= 24 => icon size = Button size - 8
// Button size > 24 && <= 32 => icon size = Button size - 12
// Button size > 32 => icon size = Button size - 16
const customIconSize = width => width <= 24 ? width - 8 : width <= 32 ? width - 12 : width - 16;
const IconLoadingView = iconSize => {
  return jsx(CircularProgress, {
    size: 'extraSmall',
    background: 'transparent',
    color: 'currentColor',
    strokeWidth: 3,
    width: parseInt(iconSize)
  });
};
const ButtonIcon = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  children,
  circular,
  className,
  color: colorProp,
  colorHover,
  disabled,
  height,
  id,
  loading,
  name,
  onClick,
  size,
  style,
  viewBox,
  viewType,
  width
}, reference) => {
  var _sizeMap$get, _sizeMap$get2;
  const ref = useRef(null);
  const rippleRef = useRef(null);
  const isColorDefault = colorProp === 'default';
  const isViewTypeGhost = viewType === 'ghost';
  const buttonSize = ((_sizeMap$get = sizeMap.get(size)) === null || _sizeMap$get === void 0 ? void 0 : _sizeMap$get.buttonSize) || '40px';
  const iconSize = width ? isNaN(width) ? '60%' : customIconSize(parseInt(width)) + 'px' : ((_sizeMap$get2 = sizeMap.get(size)) === null || _sizeMap$get2 === void 0 ? void 0 : _sizeMap$get2.iconSize) || '24px';
  const _ButtonRootCSS = ButtonRootCSS(circular, colorHover, colorProp, name, isColorDefault, viewType, loading);
  const _ButtonSizeCSS = ButtonSizeCSS(width, height, buttonSize, iconSize);
  const _ButtonIconCSS = ButtonIconCSS(iconSize);
  const _onClick = event => {
    event.persist();
    onClick && event.stopPropagation();
    if (disabled || loading) return;
    if (!isViewTypeGhost) rippleRef.current.start(event);
    onClick && onClick(event);
  };
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  return useMemo(() => {
    return jsx("button", {
      css: [_ButtonRootCSS, _ButtonSizeCSS],
      ref: ref,
      id: id,
      disabled: disabled,
      style: style,
      onClick: _onClick,
      className: classNames('DGN-UI-ButtonIcon', viewType, size, className)
    }, loading && IconLoadingView(iconSize), !loading && jsx("span", {
      className: 'DGN-UI-ButtonIcon-Icon',
      css: _ButtonIconCSS
    }, children || jsx(Icon, {
      name: name,
      width: isViewTypeGhost ? width || iconSize : iconSize,
      height: isViewTypeGhost ? height || iconSize : iconSize,
      color: 'currentColor',
      viewBox: viewBox
    })), !disabled && !loading && !isViewTypeGhost && jsx(Ripple, {
      ref: rippleRef,
      color: getRippleColor(colorProp, viewType, allColors),
      circular: circular
    }));
  }, [children, circular, className, colorHover, colorProp, disabled, height, id, loading, name, onClick, size, style, viewBox, viewType, width]);
}));
const ButtonIconCSS = iconSize => css`
    ${displayFlex};
    ${flexRow};
    ${positionRelative};
    ${boxBorder};
    ${justifyCenter};
    ${itemsCenter};
    ${parseWidthHeight(iconSize)};
    ${parseMinWidthHeight(iconSize)};
`;
const ButtonSizeCSS = (width, height, buttonSize, iconSize) => css`
    ${parseWidthHeight(width || buttonSize, height || buttonSize)};
    ${parseMinWidthHeight(width || buttonSize, height || buttonSize)};
    &.ghost {
        ${parseWidthHeight(width || iconSize, height || iconSize)};
        ${parseMinWidthHeight(width || iconSize, height || iconSize)};
    }
`;
const ButtonRootCSS = (circular, colorHover, color, name, isColorDefault, viewType, loading) => css`
    ${displayInlineFlex};
    ${positionRelative};
    ${justifyCenter};
    ${itemsCenter};
    ${outlineNone};
    ${boxBorder};
    ${cursorPointer};
    ${userSelectNone};
    ${borderRadius(circular ? '50%' : 4)};
    ${pd(0)};
    transition: background-color 0.5s, filter 0.5s;
    ${loading && pointerEventsNone};
    ${viewType === 'outlined' && css`
        ${bgColor('transparent')};
        ${border(1, color)};
        ${textColor(color)};
        ${loading && css`
            ${bgColor(hexToRGBA(color, alphaLoading))};
        `}
        ${Object.keys(alphaPseudo).map(key => css`
                    &: ${key} {
                        ${bgColor(hexToRGBA(color, alphaPseudo[key]))};
                    }
                `)}
        ${isColorDefault && css`
            ${border(1, 'system/rest')};
            ${textColor('system/rest')};
            ${loading && css`
                ${textColor(colorHover)};
                ${borderColor(colorHover)};
                ${bgColor(hexToRGBA(colorHover, alphaLoading))};
            `}
            &:hover,
            &:focus,
            &:active {
                ${textColor(colorHover)};
                ${borderColor(colorHover)};
            }
            ${Object.keys(alphaPseudo).map(key => css`
                        &: ${key} {
                            ${bgColor(hexToRGBA(colorHover, alphaPseudo[key]))};
                        }
                    `)}// &:hover | &:focus | &:active
        `}
        :disabled {
            ${borderColor('system/disabled')};
            ${bgColor('transparent')};
        }
    `}
    ${viewType === 'filled' && css`
        ${borderNone};
        ${textColor(color === getColor('system.white') ? 'system.rest' : 'system.white')};
        ${bgColor(color)};
        font-weight: bold;
        ${loading && css`
            filter: brightness(0.85);
        `}
        &:hover,
        &:focus {
            filter: brightness(0.85);
        }
        &:active {
            filter: brightness(0.7);
        }
        ${isColorDefault && css`
            ${bgColor('system/rest')};
            ${loading && bgColor('system/active')};
            &:hover,
            &:focus,
            &:active {
                ${bgColor(colorHover)};
            }
        `}
        :disabled {
            ${bgColor('fill/disabled')};
            filter: brightness(1);
        }
    `}
    ${viewType === 'text' && css`
        ${borderNone};
        ${textColor(color)};
        ${bgColor('transparent')};
        ${loading && bgColor(hexToRGBA(color, alphaLoading))};
        ${Object.keys(alphaPseudo).map(key => css`
                    &: ${key} {
                        ${bgColor(hexToRGBA(color, alphaPseudo[key]))};
                    }
                `)}
        ${isColorDefault && css`
            ${textColor('system/rest')};
            ${loading && css`
                ${textColor('system/active')};
                ${bgColor(hexToRGBA(colorHover, alphaLoading))};
            `}
            ${Object.keys(alphaPseudo).map(key => css`
                        &: ${key} {
                            ${textColor(colorHover)};
                            ${bgColor(hexToRGBA(colorHover, alphaPseudo[key]))};
                        }
                    `)}
        `}
        :disabled {
            ${bgColor('transparent')};
        }
    `}
    ${viewType === 'ghost' && css`
        ${borderNone};
        ${bgColor('transparent')};
        ${textColor(color)};
        ${loading && css`
            filter: brightness(0.85);
        `}
        &:hover,
        &:focus {
            filter: brightness(0.85);
        }
        &:active {
            filter: brightness(0.7);
        }
        ${isColorDefault && css`
            ${textColor('system/rest')};
            &:hover,
            &:focus,
            &:active {
                ${textColor(iconColorMap.get(capitalize(name)) || colorHover)};
            }
        `}
    `}
    :disabled {
        ${cursorNotAllowed};
        ${textColor('system/disabled')};
    }
`;
ButtonIcon.defaultProps = {
  circular: false,
  className: '',
  color: 'default',
  colorHover: 'system/active',
  disabled: false,
  size: 'medium',
  viewBox: true,
  viewType: 'outlined'
};
ButtonIcon.propTypes = {
  /** The content of the component. */
  children: PropTypes.node,
  /** If `true`, component is circular. */
  circular: PropTypes.bool,
  /** Class for component. */
  className: PropTypes.string,
  /** The color of the component. */
  color: PropTypes.string,
  /** Custom hover color for button with color 'default'. */
  colorHover: PropTypes.string,
  /** If `true`, the component is disabled. */
  disabled: PropTypes.bool,
  /** Height oof the component. */
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** If `true`, the loading indicator is shown. */
  loading: PropTypes.bool,
  /** Callback fired when the component is clicked. */
  onClick: PropTypes.func,
  /** Name of [icon](https://core.diginet.com.vn/ui/?path=/story/icon-basic). */
  name: PropTypes.string,
  /**
   * 	The size of the component.
   *
   * * tiny (button 24px, icon 16px)
   * * small (button 32px, icon 20px)
   * * medium (button 40px, icon 24px)
   * * large (button 48px, icon 32px)
   * * giant (button 56px, icon 40px)
   * */
  size: PropTypes.oneOf(['tiny', 'small', 'medium', 'large', 'giant', 'extraGiant']),
  /** Style inline of component. */
  style: PropTypes.object,
  /** If `true`, icon has view box. */
  viewBox: PropTypes.bool,
  /** The variant to use. */
  viewType: PropTypes.oneOf(['text', 'outlined', 'filled', 'ghost']),
  /** Width of the component. */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  /**
   * ref methods (ref.current.instance.*method*)
   *
   * * option(): Gets all UI component properties
   *  * Returns value - object
   * * option(optionName): Gets the value of a single property
   *  * @param {optionName} - string
   *  * Returns value - any
   * * option(optionName, optionValue): Updates the value of a single property
   *  * @param {optionName} - string
   *  * @param {optionValue} - any
   * * option(options): Updates the values of several properties
   *  * @param {options} - object
   */
};

export { ButtonIcon };
export default OptionWrapper(ButtonIcon);